import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, MessagesSquare, Send } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { PageError } from "@/components/PageState";
import { formErrorClass } from "@/components/ui/atoms";
import { EmptyState } from "@/components/ui/empty-state";
import { IconButton } from "@/components/ui/icon-button";
import { Textarea } from "@/components/ui/textarea";
import { AvatarDoResponsavel } from "@/components/usuarios/AvatarDoResponsavel";
import { urlDaFotoNoPortal } from "@/lib/api/ficha-da-equipe";
import { iniciaisDoNome } from "@/lib/ficha-da-equipe";
import { nomeDoEscritorio } from "@/lib/api/perfil-do-escritorio";
import { cn } from "@/lib/utils";
import type { Session } from "@/lib/types";
import { criarClienteDeChat, type ChatMessage } from "@/lib/chat-api";
import { carregarUltimasMensagens } from "@/lib/chat-ultimas-mensagens";
import { useChatStream } from "@/lib/useChatStream";
import { formatClock } from "../lib/format";

/**
 * TUT-296b — o chat próprio do PORTAL (a seção "Mensagens"). TUT-491: o
 * escritório não usa mais este corpo — `/conversas` é a caixa de entrada
 * única (`components/conversas/CaixaUnica.tsx`). TUT-545: o ícone do topo do
 * escritório mora em `ChatPanelButton.tsx`, e a "Nova conversa" da Caixa em
 * `conversas/NovaConversaDialog.tsx`; daqui o portal não alcança nem o menu
 * nem os diálogos do escritório (`portal-sem-linha.test.ts`).
 *
 * TUT-545 (decisão do dono em 27/10) — quem é de fora fala SÓ com o
 * escritório: a seção abre direto a conversa fixa
 * (`POST /portal/chat/escritorio`, idempotente), sem lista de pessoas e sem
 * "Nova conversa". O externo não vê a equipe; cada resposta diz quem escreveu
 * e de onde ("Marina · Marcal & Hespanhol").
 *
 * TUT-558 (decisão do dono em 29/10) — a resposta leva a foto de quem
 * respondeu, ao lado do nome: é o ÚNICO lugar do portal em que a foto de
 * alguém da equipe aparece, porque é onde o nome já aparece para quem é de
 * fora. Não há lista da equipe no portal; a foto é pedida pelo id do autor
 * (`urlDaFotoNoPortal`), e sem foto ficam as iniciais.
 */

function horaCurta(iso: string): string {
  return formatClock(new Date(iso));
}

/**
 * TUT-340 — acrescenta a mensagem ao histórico aberto UMA vez só.
 *
 * Ela chega por dois caminhos: a resposta do `POST` (quem enviou) e o evento
 * `chat.message` da SSE (todos os participantes, o remetente incluído). A
 * ordem entre os dois não é garantida — o servidor publica o evento antes de
 * responder —, então os dois passam por aqui e o id decide. Antes só a SSE
 * conferia, e a mensagem enviada aparecia duplicada (pedido do dono, 28/09).
 */
export function acrescentarMensagem(
  atual: { items: ChatMessage[]; total: number } | undefined,
  mensagem: ChatMessage,
): { items: ChatMessage[]; total: number } {
  if (atual?.items.some((m) => m.id === mensagem.id)) return atual;
  return {
    items: [...(atual?.items ?? []), mensagem],
    total: (atual?.total ?? 0) + 1,
  };
}

/**
 * TUT-395 — o teto da API (`api/src/domain/chat.ts`). A caixa aceitava 10.000
 * caracteres e a API recusava acima de 4.000: a mensagem longa sumia.
 */
export const LIMITE_DA_MENSAGEM = 4_000;

const FALHA_NO_ENVIO =
  "Não foi possível enviar. A mensagem continua aqui para tentar de novo.";

/**
 * TUT-395 — o texto do compositor só sai DEPOIS de o envio dar certo. Antes
 * ele era apagado no clique: se o envio falhasse, a mensagem sumia. E só sai
 * o que foi enviado — o que a pessoa digitou enquanto esperava fica.
 */
function useCompositor(onEnviar: (texto: string) => Promise<unknown>, enviando: boolean) {
  const [texto, setTexto] = useState("");
  const [erro, setErro] = useState("");
  function enviar() {
    const corpo = texto.trim();
    if (!corpo || enviando) return;
    setErro("");
    onEnviar(corpo).then(
      () => setTexto((atual) => (atual.trim() === corpo ? "" : atual)),
      () => setErro(FALHA_NO_ENVIO),
    );
  }
  return { texto, setTexto, erro, enviar };
}

/**
 * Exportado só para o teste de Enter/Shift+Enter — não é usado fora deste
 * arquivo. TUT-545: o rascunho por conversa (TUT-472) saiu daqui: no portal há
 * uma conversa só, e o compositor não desmonta enquanto ela está aberta (o da
 * Caixa Única guarda o dele em `CompositorDaCaixa`).
 */
export function Composer({
  onEnviar,
  enviando,
}: {
  onEnviar: (texto: string) => Promise<unknown>;
  enviando: boolean;
}) {
  const { texto, setTexto, erro, enviar } = useCompositor(onEnviar, enviando);

  return (
    <form
      className="border-t border-border p-2"
      onSubmit={(event) => {
        event.preventDefault();
        enviar();
      }}
    >
      <div className="flex items-end gap-2">
      <label className="sr-only" htmlFor="chat-composer">
        Escrever mensagem
      </label>
      <Textarea
        id="chat-composer"
        autoResize
        alturaMaxima={120}
        className="flex-1 py-[0.4rem]"
        maxLength={LIMITE_DA_MENSAGEM}
        value={texto}
        placeholder="Escreva uma mensagem — Enter envia, Shift+Enter quebra linha"
        onChange={(event) => setTexto(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            enviar();
          }
        }}
      />
      {/* TUT-563b — o envio era `Button size="icon"` primário; o IconButton
          não tem o primário (o plano, item 3), e o envio fica no vidro. */}
      <IconButton
        type="submit"
        papel="vidro"
        label="Enviar mensagem"
        icone={<Send aria-hidden="true" />}
        disabled={enviando || texto.trim().length === 0}
      />
      </div>
      {erro ? (
        <p role="alert" className={`mt-1 ${formErrorClass}`}>
          {erro}
        </p>
      ) : null}
    </form>
  );
}

/**
 * O corpo da seção Mensagens do portal: a conversa com o escritório, aberta.
 * O cabeçalho é o nome do escritório (o da sessão, `tenant.displayName`), ou
 * "Escritório" quando não há nome.
 */
export function ChatCore({ session }: { session: Session }) {
  const portal = true;
  const client = criarClienteDeChat(portal);
  const queryClient = useQueryClient();
  const meuId = session.user.id;
  const escritorio = nomeDoEscritorio(session) || "Escritório";

  // O POST é idempotente (a mesma conversa sempre): não há por que refazê-lo
  // ao voltar à janela.
  const conversa = useQuery({
    queryKey: ["chat", "escritorio"],
    queryFn: () => client.escritorio(),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const id = conversa.data?.id ?? null;
  const idRef = useRef(id);
  idRef.current = id;

  const mensagens = useQuery({
    queryKey: ["chat", "messages", portal, id],
    queryFn: () => carregarUltimasMensagens(client, id as string),
    enabled: !!id,
  });

  const marcarLida = useMutation({
    mutationFn: (conversationId: string) => client.markRead(conversationId),
  });

  const enviar = useMutation({
    mutationFn: (texto: string) => client.postMessage(id as string, texto),
    onSuccess: (mensagem) => {
      // TUT-340 — a MESMA porta do tempo real: a SSE costuma entregar a
      // mensagem ao próprio remetente ANTES de o POST responder, e acrescentar
      // sem conferir o id desenhava a mensagem duas vezes.
      queryClient.setQueryData<{ items: ChatMessage[]; total: number }>(
        ["chat", "messages", portal, mensagem.conversationId],
        (atual) => acrescentarMensagem(atual, mensagem),
      );
    },
  });

  const { status } = useChatStream(portal, (evento) => {
    // Tempo real: a resposta do escritório entra direto na conversa aberta, e
    // ela segue lida (a pessoa está olhando para ela). No portal não há outra
    // conversa: o evento que não é desta não entra.
    if (evento.type !== "chat.message") return;
    const dado = evento.data as { conversationId?: string } & Partial<ChatMessage>;
    if (!dado?.conversationId || !dado.id || dado.conversationId !== idRef.current) return;
    queryClient.setQueryData<{ items: ChatMessage[]; total: number }>(
      ["chat", "messages", portal, dado.conversationId],
      (atual) => acrescentarMensagem(atual, dado as ChatMessage),
    );
    if (dado.authorUserId !== meuId) marcarLida.mutate(dado.conversationId);
  });
  const statusAnterior = useRef(status);
  const listaRef = useRef<HTMLDivElement | null>(null);

  // Ao abrir a conversa, marca como lida — a mesma regra do chat de sempre.
  useEffect(() => {
    if (id) marcarLida.mutate(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // A mais nova à vista: a rolagem desce ao fim quando chega mensagem.
  useEffect(() => {
    if (listaRef.current) listaRef.current.scrollTop = listaRef.current.scrollHeight;
  }, [mensagens.data]);

  // Reconexão depois de queda: o que chegou com o canal fechado não vem pela
  // SSE, então as mensagens são refeitas (TUT-395).
  useEffect(() => {
    if (statusAnterior.current !== "open" && status === "open") {
      void queryClient.invalidateQueries({ queryKey: ["chat", "messages", portal] });
    }
    statusAnterior.current = status;
  }, [status, queryClient, portal]);

  if (conversa.isError) {
    return (
      <PageError
        message="Não foi possível abrir a conversa com o escritório."
        onRetry={() => void conversa.refetch()}
      />
    );
  }

  const itens = mensagens.data?.items ?? [];
  return (
    <div className="flex h-full flex-col">
      <header className="flex min-w-0 items-center gap-2 border-b border-border p-3">
        <Building2 aria-hidden="true" className="size-5 shrink-0 text-[var(--primary)]" />
        <div className="min-w-0">
          <h2 className="m-0 truncate text-subtitulo">{escritorio}</h2>
          <p className="m-0 text-legenda text-[var(--muted)]">
            Quem atende no escritório responde por aqui.
          </p>
        </div>
      </header>
      <div ref={listaRef} role="log" aria-label="Mensagens" className="flex-1 overflow-y-auto p-3">
        {conversa.isLoading || mensagens.isLoading ? (
          <p className="muted">Abrindo a conversa…</p>
        ) : mensagens.isError ? (
          <PageError message="Não foi possível ler as mensagens." onRetry={() => void mensagens.refetch()} />
        ) : itens.length === 0 ? (
          <EmptyState>
            <div>
              <MessagesSquare className="mx-auto size-8" aria-hidden="true" />
              <p>Escreva ao escritório: quem atende responde por aqui.</p>
            </div>
          </EmptyState>
        ) : (
          <ul className="flex flex-col gap-2">
            {itens.map((mensagem) => {
              const minha = mensagem.authorUserId === meuId;
              return (
                <li
                  key={mensagem.id}
                  className={cn("flex items-start gap-2", minha ? "justify-end" : "justify-start")}
                >
                  {!minha ? (
                    <AvatarDoResponsavel
                      userId={mensagem.authorUserId}
                      nome={mensagem.authorName}
                      iniciais={iniciaisDoNome(mensagem.authorName)}
                      rotulo=""
                      fotoUrl={urlDaFotoNoPortal(mensagem.authorUserId)}
                    />
                  ) : null}
                  <div className={cn("flex min-w-0 max-w-[80%] flex-col", minha ? "items-end" : "items-start")}>
                    <div
                      className={cn(
                        "max-w-full rounded-[10px] px-3 py-[0.4rem]",
                        minha ? "bg-primary text-primary-foreground" : "bg-surface-2",
                      )}
                    >
                      {!minha ? (
                        <small className="block text-legenda font-semibold">
                          {mensagem.authorName} · {escritorio}
                        </small>
                      ) : null}
                      <p className="whitespace-pre-wrap break-words">{mensagem.body}</p>
                    </div>
                    <small className="mt-[0.15rem] text-legenda text-[var(--muted)]">
                      {horaCurta(mensagem.createdAt)}
                    </small>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {id ? <Composer onEnviar={(texto) => enviar.mutateAsync(texto)} enviando={enviar.isPending} /> : null}
    </div>
  );
}

/** A seção "Mensagens" do portal — a conversa com o escritório, no corpo da página. */
export function ChatSection({ session }: { session: Session }) {
  return (
    <div className="h-[32rem] overflow-hidden rounded-[9px] border border-border bg-surface">
      <ChatCore session={session} />
    </div>
  );
}
